import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SiteCtaBand from "@/components/feature/SiteCtaBand";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { TopicCard } from "@/components/base/TopicCard";
import { useSeo } from "@/hooks/useSeo";
import { topics } from "@/mocks/topics";
import { categories } from "@/mocks/categories";

type SortKey = "hot" | "new" | "discuss";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "hot", label: "按热度" },
  { key: "new", label: "按更新" },
  { key: "discuss", label: "按讨论量" },
];

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const urlKeyword = searchParams.get("q") ?? "";

  const [keyword, setKeyword] = useState(urlKeyword);
  const [sort, setSort] = useState<SortKey>("hot");

  useEffect(() => {
    setKeyword(urlKeyword);
  }, [urlKeyword]);

  const activeCategoryName =
    categories.find((c) => c.slug === activeCategory)?.name ?? "全部话题";

  const filtered = useMemo(() => {
    let list = topics.filter(
      (t) => activeCategory === "all" || t.categorySlug === activeCategory
    );
    const k = keyword.trim();
    if (k) {
      list = list.filter(
        (t) =>
          t.title.includes(k) ||
          t.summary.includes(k) ||
          t.tags.some((tag) => tag.includes(k))
      );
    }
    const sorted = [...list];
    if (sort === "hot") sorted.sort((a, b) => b.heat - a.heat);
    if (sort === "new") sorted.sort((a, b) => b.date.localeCompare(a.date));
    if (sort === "discuss") sorted.sort((a, b) => b.commentCount - a.commentCount);
    return sorted;
  }, [activeCategory, keyword, sort]);

  const changeCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug === "all") {
      next.delete("category");
    } else {
      next.set("category", slug);
    }
    setSearchParams(next);
  };

  const resetFilters = () => {
    setKeyword("");
    setSearchParams(new URLSearchParams());
  };

  useSeo({
    title: `${activeCategoryName} - 话题广场 | 爆料看点`,
    description:
      "按分类浏览正在被讨论的爆料话题，支持关键词搜索与热度、更新、讨论量排序。每个话题都包含评论区高能回复、网友神评论与投票结果展示。",
    keywords:
      "话题广场,爆料看点,评论区高能回复,网友神评论,投票结果展示,舆论态度分布",
    path: "/topics",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "话题广场",
        url: "https://qrwho.cn/topics",
        inLanguage: "zh-CN",
        description:
          "按分类浏览爆料话题，包含评论区高能回复、网友神评论与投票结果展示。",
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: filtered.length,
          itemListElement: filtered.map((topic, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: topic.title,
            url: `https://qrwho.cn/topics/${topic.id}`,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://qrwho.cn/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "话题广场",
            item: "https://qrwho.cn/topics",
          },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
          <Breadcrumbs items={[{ label: "话题广场" }]} />
          <h1 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            话题广场
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            这里收录了近期讨论最集中的爆料话题。可以按栏目筛选，也可以直接搜索关键词，
            每个话题都配了完整的评论区高能回复、网友神评论与投票结果。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
        <div className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-md">
              <span className="absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" aria-hidden="true" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索话题、栏目或关键词，如：剪辑、退票"
                aria-label="搜索话题"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 font-label text-sm text-foreground-900 transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div className="inline-flex w-fit items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                    sort === option.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => changeCategory("all")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                activeCategory === "all"
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部
            </button>
            {categories.map((category) => (
              <button
                key={category.slug}
                type="button"
                onClick={() => changeCategory(category.slug)}
                className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                  activeCategory === category.slug
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={category.icon} aria-hidden="true" />
                </span>
                {category.name}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4 font-label text-xs text-foreground-500">
            <span>
              共找到 <span className="font-medium text-foreground-900">{filtered.length}</span> 个话题
            </span>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex cursor-pointer items-center gap-1 transition-colors hover:text-primary-600"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-refresh-line" aria-hidden="true" />
              </span>
              重置筛选
            </button>
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((topic, index) => (
              <TopicCard key={topic.id} topic={topic} rank={sort === "hot" ? index + 1 : undefined} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
              没有找到匹配的话题
            </h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
              换个关键词试试，或者直接返回全部话题列表。也可以从栏目按钮里挑一个感兴趣的方向。
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={resetFilters}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部话题
              </button>
              <Link
                to="/polls"
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                去看投票风向
              </Link>
            </div>
          </div>
        )}
      </div>

      <SiteCtaBand
        title="有想补充的话题分类？"
        description="如果你觉得还有哪类事件值得单独开一个栏目，或者某个话题缺少后续进展，都欢迎发邮件告诉我们。"
      />
    </SiteLayout>
  );
}